import React, { ChangeEvent, useState } from 'react';
import { Formik } from 'formik';
import { Box, FormControl, FormGroup, InputLabel, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash/fp';
import { useApolloClient } from '@apollo/client';
import { CheckboxField, InputField, SelectField } from '../../../../components/forms/fields';
import FormWrapper from '../../../../components/forms/FormWrapper';
import { sanitizeFormValues } from '../../../../utils';
import { FormSubmitType } from '../../../../types';
import { EditFormLayout } from '../../../../ui-kit';
import { UnitType, WarehouseCardFormQuery, WarehousesFragment, WarehousesFragmentDoc } from '../../../../generated/graphql';
import { newWarehouseCardValidationScheme } from './validation';
import { WarehouseCardFormValues } from './types';

const unitTypesWithoutPcs: Array<{ id: UnitType; name: UnitType }> = [];

Object.values(UnitType).forEach(unitType => {
    if (UnitType.Pcs !== unitType) {
        unitTypesWithoutPcs.push({
            id: unitType,
            name: unitType,
        });
    }
});

const unitTypes: Array<{ id: UnitType; name: UnitType }> = [{ id: UnitType.Pcs, name: UnitType.Pcs, }, ...unitTypesWithoutPcs];
const onlyPiecesUnitTypes: Array<{ id: UnitType; name: UnitType }> = [{ id: UnitType.Pcs, name: UnitType.Pcs, }];

type OnSubmit = FormSubmitType<WarehouseCardFormValues>;

const CONVERTED_UNIT_FIELD = 'convertedUnit';
const CONVERTED_QUANTITY_FIELD = 'convertedQuanitity';

interface WarehouseFormProps {
    onSubmit: OnSubmit;
    onClose: () => void;
    warehouses: WarehouseCardFormQuery['warehouses'];
    taxes: WarehouseCardFormQuery['taxes'];
    categories: WarehouseCardFormQuery['stockCardCategories'];
    initialValues?: Partial<WarehouseCardFormValues>;
    currentWarehouseId: string;
    requiredWarehouseIds?: string[];
    currentTaxId: string;
    buttonText: string;
    dialogName: string;
    isUpdate?: boolean;
    onlyPieces?: boolean;
    onRemove?(): void;
}

// TODO: add placeholders for inputs

const WarehouseCardFormView = ({
    warehouses,
    onSubmit,
    initialValues,
    currentWarehouseId,
    requiredWarehouseIds,
    onClose,
    buttonText,
    dialogName,
    taxes,
    categories,
    currentTaxId,
    onlyPieces,
    onRemove,
    isUpdate = false,
}: WarehouseFormProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const client = useApolloClient();
    const { t } = useTranslation();

    const [storageUnitType, setStorageUnitType] = useState(UnitType.Pcs);
    const defaultValues: WarehouseCardFormValues = {
        name: '',
        sku: '',
        storageUnitId: UnitType.Pcs,
        taxId: currentTaxId,
        convertedQuanitity: null,
        convertedUnit: null,
        categoryId: null,
        warehouseIds: [currentWarehouseId],
    };

    const currentWarehouseFragment = client.readFragment<WarehousesFragment>({
        id: `Warehouse:${currentWarehouseId}`,
        fragment: WarehousesFragmentDoc,
    });

    let priceWithoutVatHelperText: undefined | string;

    if (warehouses.length > 1) {
        if (isUpdate) {
            priceWithoutVatHelperText = currentWarehouseFragment
                ? t(`Price in warehouse "{{warehouseName}}"`, { warehouseName: currentWarehouseFragment.name })
                : t('Price in current warehouse');
        } else {
            priceWithoutVatHelperText = t('Price is being set to all selected warehouses');
        }
    }


    const handleSubmit = async (values: WarehouseCardFormValues) => {
        const finalValues = sanitizeFormValues(values);

        if (!isEmpty(finalValues)) {
            await onSubmit(finalValues);
        }

    };

    const flexDirection = isDesktop ? 'row' : 'column';

    return (
        <EditFormLayout
            isDialogView
            onClose={onClose}
            title={dialogName}
            buttonText={t('Remove')}
            onRemove={onRemove}
        >
            <Formik
                initialValues={{ ...defaultValues, ...initialValues }}
                validationSchema={newWarehouseCardValidationScheme}
                onSubmit={handleSubmit}
            >
                {formikProps => (
                    <FormWrapper submitText={buttonText} isSubmitting={formikProps.isSubmitting} isDialogView>
                        <Box my={2} width="100%" display="flex" flexDirection="column">
                            <Box display="flex" flexDirection={flexDirection}>
                                <InputField<WarehouseCardFormValues> name="name" label={t('Name')} formikProps={formikProps} required />
                                <Box mr={2} />
                                <InputField<WarehouseCardFormValues>
                                    tooltip={t('Stock Keeping Unit is a unique identifier preferably provide by a supplier')}
                                    name="sku"
                                    label={t('SKU')}
                                    formikProps={formikProps}
                                />
                            </Box>
                            <SelectField
                                required
                                disabled={onlyPieces || isUpdate}
                                onChange={(e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
                                    const nextValue = e.target.value as UnitType;
                                    setStorageUnitType(nextValue);
                                    if (nextValue !== UnitType.Pcs) {
                                        formikProps.setFieldValue(CONVERTED_UNIT_FIELD, null);
                                        formikProps.setFieldValue(CONVERTED_QUANTITY_FIELD, null);
                                    }
                                    formikProps.handleChange(e);
                                }}
                                variant="outlined"
                                options={onlyPieces ? onlyPiecesUnitTypes : unitTypes}
                                name="storageUnitId"
                                label={t('Storage unit')}
                                formikProps={formikProps}
                            />
                            {storageUnitType === UnitType.Pcs && (
                            <Box display="flex" flexDirection={flexDirection}>
                                <InputField<WarehouseCardFormValues>
                                    tooltip={t('1 piece equals converted quantity in alternative unit')}
                                    disabled={isUpdate}
                                    name={CONVERTED_QUANTITY_FIELD}
                                    label={t('Converted amount')}
                                    formikProps={formikProps}
                                />
                                <Box mr={2} />
                                <SelectField
                                    tooltip={
                                        t('Alternative unit of measure that allows you to count stock and recipes not only in pieces')
                                    }
                                    disabled={isUpdate}
                                    variant="outlined"
                                    options={unitTypesWithoutPcs}
                                    name={CONVERTED_UNIT_FIELD}
                                    label={t('Alternative unit')}
                                    formikProps={formikProps}
                                />
                            </Box>
                            )}
                            <SelectField
                                variant="outlined"
                                options={categories}
                                name="categoryId"
                                label={t('Warehouse category')}
                                formikProps={formikProps}
                            />
                            <Box display="flex" mb={2} flexDirection={flexDirection}>
                                <InputField
                                    name="priceWithoutVat"
                                    helpertext={priceWithoutVatHelperText}
                                    label={t('Price buy without VAT')}
                                    formikProps={formikProps}
                                    type="number"
                                />
                                <Box mr={2} />
                                <SelectField
                                    required
                                    variant="outlined"
                                    options={taxes}
                                    name="taxId"
                                    label={t('Tax buy')}
                                    formikProps={formikProps}
                                />
                            </Box>
                            <InputLabel>{t('Available in warehouses')}</InputLabel>
                            <FormControl fullWidth variant="outlined" size='small'>
                                <FormGroup>
                                    {warehouses.map(({ id: warehouseId, name }) => {
                                        const isDefaultPreselected = requiredWarehouseIds
                                            ? !!requiredWarehouseIds.find(whId => whId === warehouseId)
                                            : false;
                                        const isDefaultChecked = requiredWarehouseIds
                                            ? isDefaultPreselected || currentWarehouseId === warehouseId
                                            : false;
                                        return (
                                            <CheckboxField
                                                key={warehouseId}
                                                formikProps={formikProps}
                                                disabled={isDefaultPreselected}
                                                name="warehouseIds"
                                                defaultChecked={isDefaultChecked}
                                                value={warehouseId}
                                                label={name}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </FormControl>
                        </Box>
                    </FormWrapper>
                )}
            </Formik>
        </EditFormLayout>
    );
};

export default WarehouseCardFormView;
